import type { ChargeEligibility, LotRef, PlacementOpenInvoice, ReassignTarget, SaleState } from '@neon-one/auctions-types';

import type { PaymentStatus } from './types';

export const ReassignAddress = { Line: 'line', Lot: 'lot' } as const;

interface ReassignSubjectContext {
    lotRef: LotRef;
    lot: string | null;
    itemTitle: string;
    holderName: string | null;
    invoiceNumber: string | null;
    amountCents: number;
    refundedCents: number;
    fmvCents: number | null;
    saleState?: SaleState;
}

export interface LineReassignSubject extends ReassignSubjectContext {
    address?: typeof ReassignAddress.Line;
    lineId: string;
    invoiceUuid: string;
    status: PaymentStatus;
}

export interface LotReassignSubject extends ReassignSubjectContext {
    address: typeof ReassignAddress.Lot;
    lineId: null;
    invoiceUuid: null;
    holderName: null;
    invoiceNumber: null;
    status: null;
    saleState: SaleState;
}

export type ReassignSubject = LineReassignSubject | LotReassignSubject;

export interface TargetRow {
    bidderUuid: string;
    name: string;
    amountCents: number;
    inherited: boolean;
    consequence: ReassignTarget['consequence'];
    openInvoiceUuid: string | null;
    openInvoiceNumber: string | null;
    email: string | null;
    cardLabel: string | null;
    chargeEligibility: ChargeEligibility | null;
    bidCents: number | null;
    openInvoice: PlacementOpenInvoice | null;
}

// Above this many candidates the pool stops being scannable, so the search
// field appears. Below it, searching a list you can already see is noise.
export const SEARCH_THRESHOLD = 8;

const DEFAULT_FIRST_NAME = 'bidder';

export function toTargetRow(target: ReassignTarget, inheritedPriceCents: number): TargetRow {
    const bid = target.bidCents === undefined ? target.amountCents : target.bidCents;
    return {
        bidderUuid: target.bidderUuid,
        name: target.name,
        amountCents: bid ?? inheritedPriceCents,
        inherited: bid === null,
        consequence: target.consequence,
        openInvoiceUuid: target.openInvoiceUuid,
        openInvoiceNumber: target.openInvoiceNumber,
        email: target.email ?? null,
        cardLabel: target.cardLabel ?? null,
        chargeEligibility: target.chargeEligibility ?? null,
        bidCents: bid,
        openInvoice: target.openInvoice ?? null,
    };
}

// Buttons and notes address the target by first name; a blank one still has to
// read as a sentence rather than as a gap.
export function firstNameOf(name: string | null | undefined): string {
  return (name ?? '').split(' ')[0] || DEFAULT_FIRST_NAME;
}
